import { supabase } from '../supabaseClient';
import { validateFile, sanitizeFileName, getFileType, formatFileSize } from './fileUploadConfig';

export class FileUploadService {
  
  static async uploadFile(file, surveyProjectId, userId) {
    try {
      // Validate file and determine type
      const fileType = getFileType(file);
      if (!fileType) {
        throw new Error('Unsupported file type');
      }
      
      validateFile(file, fileType);
      
      // Sanitize filename
      const sanitizedName = sanitizeFileName(file.name);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${sanitizedName}`;
      
      // Determine bucket and path
      const bucket = fileType === 'photos' ? 'survey-photos' : 'survey-documents';
      const filePath = `${surveyProjectId}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      
      // Prepare database record
      const dbRecord = {
        survey_project_id: surveyProjectId,
        file_name: file.name,
        file_path: urlData.publicUrl,
        file_size: file.size,
        uploaded_by: userId
      };
      
      // Insert into appropriate table
      const tableName = fileType === 'photos' ? 'survey_photos' : 'survey_documents';
      
      // Add file_type for documents only (photos table may not have this column)
      if (fileType === 'documents') {
        dbRecord.file_type = file.type;
      }
      
      // For photos, add additional metadata
      if (fileType === 'photos') {
        try {
          const imageData = await FileUploadService.extractImageMetadata(file);
          dbRecord.image_width = imageData.width;
          dbRecord.image_height = imageData.height;
          dbRecord.captured_at = imageData.capturedAt;
        } catch (metaError) {
          console.warn('Could not extract image metadata:', metaError);
        }
      }
      
      const { data: dbData, error: dbError } = await supabase
        .from(tableName)
        .insert([dbRecord])
        .select()
        .single();
      
      if (dbError) {
        // If database insert fails, clean up the uploaded file
        await supabase.storage.from(bucket).remove([filePath]);
        throw new Error(`Database error: ${dbError.message}`);
      }
      
      return {
        success: true,
        data: {
          ...dbData,
          fileType,
          formattedSize: formatFileSize(file.size)
        }
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  static async uploadMultipleFiles(files, surveyProjectId, userId, onProgress = null) {
    const results = [];
    const totalFiles = files.length;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const result = await FileUploadService.uploadFile(file, surveyProjectId, userId);
      
      results.push({
        fileName: file.name,
        ...result
      });
      
      if (onProgress) {
        onProgress(i + 1, totalFiles, file.name, result.success);
      }
    }
    
    return results;
  }
  
  static async deleteFile(fileId, fileType, filePath) {
    try {
      const tableName = fileType === 'photos' ? 'survey_photos' : 'survey_documents';
      const bucket = fileType === 'photos' ? 'survey-photos' : 'survey-documents';
      
      // Extract file path from URL
      const urlPath = filePath.split('/').slice(-2).join('/'); // Get last 2 parts: projectId/filename
      
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(bucket)
        .remove([urlPath]);
      
      if (storageError) {
        console.warn('Storage deletion error:', storageError);
      }
      
      // Delete from database
      const { error: dbError } = await supabase
        .from(tableName)
        .delete()
        .eq('id', fileId);
      
      if (dbError) {
        throw new Error(`Database deletion failed: ${dbError.message}`);
      }
      
      return { success: true };
      
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  static async getProjectFiles(surveyProjectId) {
    try {
      // Fetch documents and photos
      const [documentsResult, photosResult] = await Promise.all([
        supabase
          .from('survey_documents')
          .select('*')
          .eq('survey_project_id', surveyProjectId)
          .order('created_at', { ascending: false }),
        supabase
          .from('survey_photos')
          .select('*')
          .eq('survey_project_id', surveyProjectId)
          .order('created_at', { ascending: false })
      ]);
      
      if (documentsResult.error) throw documentsResult.error;
      if (photosResult.error) throw photosResult.error;
      
      // Add file type and formatted size to each file
      const documents = documentsResult.data.map(doc => ({
        ...doc,
        fileType: 'documents',
        formattedSize: formatFileSize(doc.file_size)
      }));
      
      const photos = photosResult.data.map(photo => ({
        ...photo,
        fileType: 'photos',
        formattedSize: formatFileSize(photo.file_size)
      }));
      
      return {
        success: true,
        data: {
          documents,
          photos,
          totalFiles: documents.length + photos.length
        }
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
  
  static async extractImageMetadata(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Try to get EXIF data for capture date
        let capturedAt = null;
        if (file.lastModified) {
          capturedAt = new Date(file.lastModified).toISOString();
        }
        
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
          capturedAt
        });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }
  
  static async generateThumbnail(file, maxWidth = 200, maxHeight = 200, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        // Calculate dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to generate thumbnail'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for thumbnail'));
      };
      
      img.src = url;
    });
  }
}